import { useContext, useRef, useState } from "react";
import { Box, Button, InputBase } from "@mui/material";
import { AuthContext } from "src/utils/AuthContext.js";
import { customEvent } from "src/utils/gtag.js";
import CalloutBand from "src/components/blocks/CalloutBand.js";
import FeedbackForm from "src/components/blocks/dialogs/FeedbackForm.js";

/**
 * Request-a-calendar capsule: type the missing team, show, or event and the
 * embedded Request button opens the standard FeedbackForm dialog with the text
 * prefilled, submitting to the calendar-requests intake. Renders as the
 * end-of-home band by default; `bare` renders just the capsule for embedding
 * in other surfaces, and `source` tags the intake and analytics with the
 * surface it came from.
 *
 * @param {{ bare?: boolean, source?: string, dark?: boolean }} props
 *   `dark` outlines the capsule and button for dark surfaces.
 */
const RequestCalendarBand = ({ bare = false, source = "home-request-band", dark = false }) => {
  const { user } = useContext(AuthContext);
  const [request, setRequest] = useState("");
  const triggerRef = useRef(null);

  // Same capsule construction as the topbar search: input + attached button.
  const capsule = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        flex: "1 1 320px",
        width: "100%",
        height: "44px",
        borderRadius: "999px",
        overflow: "hidden",
        border: (theme) => (dark ? "1.5px solid #fff" : `1.5px solid ${theme.palette.text.primary}`),
        backgroundColor: "#fff",
      }}
    >
      <InputBase
        value={request}
        onChange={(e) => setRequest(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") triggerRef.current?.click();
        }}
        placeholder={'Try "Ryder Cup", "The Bear season 5", "Coachella"'}
        inputProps={{ "aria-label": "Calendar to request" }}
        sx={{ flex: 1, px: 2.5, fontSize: "15px" }}
      />
      <FeedbackForm
        category="calendar-requests"
        title="Request this calendar"
        submitLabel="Request calendar"
        defaultEmail={user?.email || ""}
        subjectLabel="What should we build?"
        initialSubject={request.trim()}
        context={{ source }}
        onSubmitted={() =>
          customEvent({
            name: "calendar_request_submitted",
            category: "Engagement",
            label: `Calendar Request: ${source}`,
            data: {
              ...(user ? { user_id: user._id, user_email: user.email } : {}),
            },
          })
        }
        trigger={
          <Button
            ref={triggerRef}
            sx={{
              height: "44px",
              borderRadius: 0,
              px: 3.25,
              fontWeight: 700,
              fontSize: "14px",
              letterSpacing: "0.04em",
              color: "#fff",
              backgroundColor: "text.primary",
              ...(dark && { border: "1.5px solid #fff", borderRight: "none" }),
              "&:hover": { backgroundColor: "#000" },
            }}
          >
            Request
          </Button>
        }
      />
    </Box>
  );

  if (bare) return capsule;

  return (
    <CalloutBand
      component="section"
      sx={{ mt: 6, mb: 5 }}
      title="If we don't have it, we build it."
      subtitle="Every request gets a real calendar, kept up to date."
      action={capsule}
    />
  );
};

export default RequestCalendarBand;
